import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { authUtils } from '../utils/auth';
import { api } from '../services/api';
import { IMAGES } from '../config/images';
import Footer from '../components/Footer';

const Dashboard = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({
    serviceType: 'all',
    urgency: 'all'
  });
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, requests]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const token = authUtils.getToken();
      const data = await api.getRequests(token);
      setRequests(data.requests || data || []);
      setError('');
    } catch (error) {
      setError('Failed to load requests. Please try again.');
      console.error('Error fetching requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    if (filters.serviceType !== 'all') {
      filtered = filtered.filter(
        (req) => req.serviceType === filters.serviceType || req.emergency === filters.serviceType
      );
    }

    if (filters.urgency !== 'all') {
      filtered = filtered.filter((req) => req.urgency?.toLowerCase() === filters.urgency.toLowerCase());
    }

    setFilteredRequests(filtered);
  };

  const handleLogout = () => {
    authUtils.removeToken();
    navigate('/admin/login');
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    handleLogout();
  };

  const toggleAccordion = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getServiceTypes = () => {
    const types = new Set();
    requests.forEach((req) => {
      if (req.serviceType) types.add(req.serviceType);
      if (req.emergency) types.add(req.emergency);
    });
    return Array.from(types);
  };

  // Calculate metrics
  const getMetrics = () => {
    const total = requests.length;
    const emergency = requests.filter((r) => r.urgency?.toLowerCase() === 'emergency').length;
    const urgent = requests.filter((r) => r.urgency?.toLowerCase() === 'urgent').length;
    const standard = requests.filter((r) => r.urgency?.toLowerCase() === 'standard').length;
    
    const serviceTypeCount = {};
    requests.forEach((r) => {
      const type = r.serviceType || r.emergency || 'Other';
      serviceTypeCount[type] = (serviceTypeCount[type] || 0) + 1;
    });

    return { total, emergency, urgent, standard, serviceTypeCount };
  };

  const metrics = getMetrics();

  const getUrgencyBadgeColor = (urgency) => {
    switch (urgency?.toLowerCase()) {
      case 'emergency':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'urgent':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'standard':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page min-h-screen flex flex-col">
      <Helmet>
        <title>Admin Dashboard | Metro Plumbing & Heating</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={IMAGES.logo} alt="Metro Plumbing & Heating" className="h-16" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Service Request Management</p>
              </div>
            </div>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 md:px-6 md:py-3 rounded-lg font-semibold flex items-center space-x-1 md:space-x-2 transition duration-300 shadow-lg hover:shadow-xl text-sm md:text-base"
            >
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline">Logout</span>
              <span className="sm:hidden">Exit</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h2>
          <p className="text-gray-600 mb-6">Monitor and manage all service requests</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Requests */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 animate-fade-in-up">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-right">
                <p className="text-blue-100 text-sm font-medium">Total</p>
                <p className="text-4xl font-bold">{metrics.total}</p>
              </div>
            </div>
            <p className="text-blue-100 text-sm font-medium">Total Requests</p>
            <div className="mt-2 pt-2 border-t border-blue-400/30">
              <p className="text-xs text-blue-200">All service requests</p>
            </div>
          </div>

          {/* Emergency */}
          <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="text-right">
                <p className="text-red-100 text-sm font-medium">Critical</p>
                <p className="text-4xl font-bold">{metrics.emergency}</p>
              </div>
            </div>
            <p className="text-red-100 text-sm font-medium">Emergency Requests</p>
            <div className="mt-2 pt-2 border-t border-red-400/30">
              <p className="text-xs text-red-200">{metrics.total > 0 ? Math.round((metrics.emergency / metrics.total) * 100) : 0}% of total</p>
            </div>
          </div>

          {/* Within 24 Hours */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-right">
                <p className="text-orange-100 text-sm font-medium">High Priority</p>
                <p className="text-4xl font-bold">{metrics.urgent}</p>
              </div>
            </div>
            <p className="text-orange-100 text-sm font-medium">Urgent Requests</p>
            <div className="mt-2 pt-2 border-t border-orange-400/30">
              <p className="text-xs text-orange-200">{metrics.total > 0 ? Math.round((metrics.urgent / metrics.total) * 100) : 0}% of total</p>
            </div>
          </div>

          {/* Flexible */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-right">
                <p className="text-green-100 text-sm font-medium">Regular</p>
                <p className="text-4xl font-bold">{metrics.standard}</p>
              </div>
            </div>
            <p className="text-green-100 text-sm font-medium">Standard Requests</p>
            <div className="mt-2 pt-2 border-t border-green-400/30">
              <p className="text-xs text-green-200">{metrics.total > 0 ? Math.round((metrics.standard / metrics.total) * 100) : 0}% of total</p>
            </div>
          </div>
        </div>

        {/* Service Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {Object.entries(metrics.serviceTypeCount).slice(0, 3).map(([service, count], index) => (
            <div key={service} className="card p-6 hover:shadow-lg transition-shadow duration-300 animate-fade-in-up" style={{ animationDelay: `${0.4 + index * 0.1}s` }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-gray-700 font-semibold text-sm">{service}</h3>
                <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">{count}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${metrics.total > 0 ? (count / metrics.total) * 100 : 0}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{metrics.total > 0 ? Math.round((count / metrics.total) * 100) : 0}% of requests</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 animate-fade-in-up border border-gray-200">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <h2 className="text-xl font-bold text-gray-900">Filters</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Service Type</label>
              <select
                value={filters.serviceType}
                onChange={(e) => setFilters({ ...filters, serviceType: e.target.value })}
                className="input-field"
              >
                <option value="all">All Services</option>
                {getServiceTypes().map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Urgency</label>
              <select
                value={filters.urgency}
                onChange={(e) => setFilters({ ...filters, urgency: e.target.value })}
                className="input-field"
              >
                <option value="all">All Urgency Levels</option>
                <option value="emergency">Emergency</option>
                <option value="urgent">Urgent</option>
                <option value="standard">Standard</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-gray-600 flex items-center">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Showing <strong className="mx-1">{filteredRequests.length}</strong> of <strong className="mx-1">{requests.length}</strong> requests
            </p>
            <button
              onClick={() => setFilters({ serviceType: 'all', urgency: 'all' })}
              className="text-sm text-white bg-primary hover:bg-blue-700 px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 text-red-800 border border-red-200 rounded-lg p-4 mb-6 animate-fade-in-up">
            <div className="flex items-start">
              <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold">{error}</p>
                <button onClick={fetchRequests} className="text-sm underline mt-2">
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Requests List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Service Requests</h2>
              <p className="text-gray-600 mt-1">View and manage customer service requests</p>
            </div>
          </div>
          
          {filteredRequests.length === 0 ? (
            <div className="card p-12 text-center">
              <svg className="w-24 h-24 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-xl text-gray-600">No requests found</p>
              {(filters.serviceType !== 'all' || filters.urgency !== 'all') && (
                <button
                  onClick={() => setFilters({ serviceType: 'all', urgency: 'all' })}
                  className="btn-primary mt-4"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            filteredRequests.map((request, index) => (
              <div
                key={request.id || index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl border border-gray-200 hover:border-primary-400 transition-all duration-300 animate-fade-in-up overflow-hidden"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Accordion Header */}
                <div
                  onClick={() => toggleAccordion(request.id || index)}
                  className="p-6 cursor-pointer flex items-center justify-between hover:bg-blue-50/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-3 mb-3">
                      <h3 className="text-lg font-bold text-gray-900">{request.name || request.customerName || request.fullName || 'N/A'}</h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${getUrgencyBadgeColor(
                          request.urgency
                        )}`}
                      >
                        {request.urgency || 'Emergency'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center bg-blue-50 text-blue-700 px-3 py-2 rounded-lg border border-blue-200">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-medium truncate">{request.serviceType || request.emergency}</span>
                      </div>
                      <div className="flex items-center bg-green-50 text-green-700 px-3 py-2 rounded-lg border border-green-200">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="font-medium">{request.phone}</span>
                      </div>
                      <div className="flex items-center bg-red-50 text-red-700 px-3 py-2 rounded-lg border border-red-200">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-medium">{request.postcode}</span>
                      </div>
                      {request.createdAt && (
                        <div className="flex items-center bg-purple-50 text-purple-700 px-3 py-2 rounded-lg border border-purple-200">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">{new Date(request.createdAt).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    <svg 
                      className="w-6 h-6 text-gray-400 transform transition-transform duration-300" 
                      style={{ transform: expandedId === (request.id || index) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Accordion Content */}
                {expandedId === (request.id || index) && (
                  <div className="px-6 pb-6 border-t border-gray-200 animate-fade-in-up">
                    <div className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Contact Information</h4>
                        <div className="space-y-2 text-sm">
                          <p>
                            <span className="font-semibold text-gray-700">Name:</span> {request.name || request.customerName || request.fullName || 'Not provided'}
                          </p>
                          <p>
                            <span className="font-semibold text-gray-700">Phone:</span>{' '}
                            <a href={`tel:${request.phone}`} className="text-primary hover:underline">
                              {request.phone}
                            </a>
                          </p>
                          {request.email && (
                            <p>
                              <span className="font-semibold text-gray-700">Email:</span>{' '}
                              <a href={`mailto:${request.email}`} className="text-primary hover:underline">
                                {request.email}
                              </a>
                            </p>
                          )}
                          <p>
                            <span className="font-semibold text-gray-700">Address:</span> {request.address}
                          </p>
                          <p>
                            <span className="font-semibold text-gray-700">Postcode:</span> {request.postcode}
                          </p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Service Details</h4>
                        <div className="space-y-2 text-sm">
                          <p>
                            <span className="font-semibold text-gray-700">Service Type:</span>{' '}
                            {request.serviceType || request.emergency}
                          </p>
                          <p>
                            <span className="font-semibold text-gray-700">Urgency:</span> {request.urgency || 'Emergency'}
                          </p>
                          {request.createdAt && (
                            <p>
                              <span className="font-semibold text-gray-700">Submitted:</span>{' '}
                              {new Date(request.createdAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                      <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">{request.description}</p>
                    </div>
                    <div className="mt-6 flex space-x-4">
                      <a href={`tel:${request.phone}`} className="btn-primary flex-1 text-center flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>Call Customer</span>
                      </a>
                      {request.email && (
                        <a href={`mailto:${request.email}`} className="btn-secondary flex-1 text-center flex items-center justify-center space-x-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>Send Email</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-fade-in-up">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Confirm Logout</h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to logout from the admin dashboard?
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Yes, Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
